import { c as _c } from "react/compiler-runtime";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/config/react-query";
import createId from "@/lib/create-id";
import { toast } from "@/lib/use-toast";
import type { ExplicitMeasurementGoal, MeasurementGoal, MeasurementGoalCreate, PatientMeasurementsResponse } from "@/types/measurement";
import { measurementQueryKeys } from "./measurements";
import { deleteMeasurementGoal, getPatientGoals, setMeasurementGoal } from "@/api/provider/user/patient/measurement-goal";
import { patientQueryKeys } from "@/api/provider/user/patient/patient-query";
import { invalidateByKey, revertOnError, startOptimisticChange } from "@/api/utils/query-helpers";
export const measurementGoalQueryKeys = {
  all: ["user", "patient", "measurement-goals"] as const,
  patient: (patientId: string) => [...measurementGoalQueryKeys.all, patientId] as const
};
export const measurementGoalQueryOptions = {
  patient: (patientId: string) => ({
    queryKey: measurementGoalQueryKeys.patient(patientId),
    queryFn: () => getPatientGoals(patientId),
    select: (data: ExplicitMeasurementGoal[] | null) => data ?? [],
    enabled: !!patientId
  })
};
export const useSetMeasurementGoalMutation = patientId => {
  const $ = _c(2);
  let t0;
  if ($[0] !== patientId) {
    t0 = {
      mutationFn: _temp,
      onMutate: async data_0 => {
        const goalId = data_0.id || createId("mgl");
        const key = measurementQueryKeys.patient(patientId);
        const {
          previous
        } = await startOptimisticChange(key, () => {
          queryClient.setQueryData(key, old => {
            if (!old) {
              return old;
            }
            const optimisticGoal = {
              isComputed: false,
              id: goalId,
              patientId: data_0.patientId,
              fieldId: data_0.fieldId,
              goalValue: data_0.goalValue ?? null,
              goalMin: data_0.goalMin ?? null,
              goalMax: data_0.goalMax ?? null,
              label: data_0.label ?? null,
              creatorId: null,
              createdDate: new Date().toISOString(),
              updatedDate: new Date().toISOString(),
              isArchived: false
            };
            return {
              ...old,
              values: old.values.map(v => v.fieldId === data_0.fieldId ? {
                ...v,
                goal: optimisticGoal
              } : v)
            };
          });
        });
        return {
          previous
        };
      },
      onError: (_err, _vars, context) => {
        if (context?.previous) {
          revertOnError(measurementQueryKeys.patient(patientId), context.previous);
        }
        toast({
          title: "Error",
          description: "Failed to set measurement goal",
          variant: "error"
        });
      },
      onSettled: () => {
        invalidateByKey(measurementGoalQueryKeys.patient(patientId));
        invalidateByKey(measurementQueryKeys.patient(patientId));
        invalidateByKey(patientQueryKeys.detail(patientId));
      }
    };
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useDeleteMeasurementGoalMutation = patientId => {
  const $ = _c(2);
  let t0;
  if ($[0] !== patientId) {
    t0 = {
      mutationFn: goalId => deleteMeasurementGoal(patientId, goalId),
      onMutate: async goalId_0 => {
        const key = measurementQueryKeys.patient(patientId);
        const {
          previous
        } = await startOptimisticChange(key, () => {
          queryClient.setQueryData(key, old => {
            if (!old) {
              return old;
            }
            return {
              ...old,
              values: old.values.map(v => v.goal && !v.goal.isComputed && v.goal.id === goalId_0 ? {
                ...v,
                goal: null
              } : v)
            };
          });
        });
        return {
          previous
        };
      },
      onError: (_err, _vars, context) => {
        if (context?.previous) {
          revertOnError(measurementQueryKeys.patient(patientId), context.previous);
        }
        toast({
          title: "Error",
          description: "Failed to remove measurement goal",
          variant: "error"
        });
      },
      onSettled: () => {
        invalidateByKey(measurementGoalQueryKeys.patient(patientId));
        invalidateByKey(measurementQueryKeys.patient(patientId));
        invalidateByKey(patientQueryKeys.detail(patientId));
      }
    };
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
function _temp(data) {
  return setMeasurementGoal(data);
}